import { useContext, useEffect, useState } from "react";
import { UserContext } from "../../components/UserContext";
import BookingCard from "../../components/BookingCard";
import { BookingData } from "../../utils/bookingUtils";
import axios from "axios";
import Cookies from "js-cookie";

interface BookingResponse {
  success: boolean;
  message: string;
  bookings: BookingData[];
}

export default function PastBookingsPage() {
  const { user } = useContext(UserContext);
  const [bookings, setBookings] = useState<BookingData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBookings = async () => {
      if (!user) return;

      try {
        const token = Cookies.get("authToken");
        const response = await axios.get<BookingResponse>(`/api/booking/list`, {
          params: {
            UserId: user.id,
            DateFilter: "past",
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data.success) {
          // Sort by end date (most recent first)
          const sortedBookings = response.data.bookings.sort(
            (a, b) =>
              new Date(b.endDate).getTime() - new Date(a.endDate).getTime()
          );
          setBookings(sortedBookings);
          setError(null);
        } else {
          setError(response.data.message || "Failed to fetch bookings");
        }
      } catch (err) {
        console.error("Error fetching past bookings:", err);
        setError("An error occurred while fetching your past bookings");
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading your past bookings...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center py-12">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
              <svg
                className="w-12 h-12 text-red-600 mx-auto mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h3 className="text-lg font-medium text-red-900 mb-2">
                Error Loading Past Bookings
              </h3>
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Past Bookings
          </h1>
          <p className="text-gray-600">
            View your completed and past workspace reservations and tours
          </p>
        </div>

        {/* Bookings Count */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-medium text-gray-900">
                Your Booking History
              </h2>
              <p className="text-gray-600">
                You have {bookings.length} past{" "}
                {bookings.length === 1 ? "booking" : "bookings"}
              </p>
            </div>
            <div className="bg-gray-100 text-gray-800 text-2xl font-bold px-4 py-2 rounded-lg">
              {bookings.length}
            </div>
          </div>
        </div>

        {/* Bookings Grid */}
        {bookings.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {bookings.map((booking) => (
              <BookingCard key={booking.id} {...booking} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 max-w-lg mx-auto">
              <svg
                className="w-16 h-16 text-gray-400 mx-auto mb-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h3 className="text-xl font-medium text-gray-900 mb-2">
                No Past Bookings
              </h3>
              <p className="text-gray-600 mb-6">
                You don't have any past bookings yet. Your completed
                reservations and tours will appear here.
              </p>
              <a
                href="/"
                className="secondary-button inline-flex items-center gap-2"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                Browse Workspaces
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
